import React from "react";
import { View, Text, StyleSheet, Pressable, Linking, Platform } from "react-native";
import Svg, { Path } from "react-native-svg";
import { useNavigation } from "@react-navigation/native";
import routes from "../../../navigation/routes";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import { WebClass, FONT_DISPLAY, FONT_MONO } from "../voxcraft/_shared";
import NtVoxcraftBrand from "../voxcraft/NtVoxcraftBrand";

// Each link slides right + brightens on hover (CSS in web/index.html).
function FootLink({ title, onPress, theme }) {
  return (
    <WebClass className="voxcraft-foot-link" style={styles.linkWrap}>
      <Pressable onPress={onPress}>
        <Text style={[styles.linkText, { color: theme.textDim }]}>{title}</Text>
      </Pressable>
    </WebClass>
  );
}

function NtMarketingPageContainerFooter() {
  const nav = useNavigation();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const company = [
    { id: "aboutus", title: "About Us", action: () => nav.navigate(routes.aboutus) },
  ];

  const other = [
    { id: "nooz",    title: "NOOZ.ai",        action: () => Linking.openURL("https://nooz.ai/latest") },
    { id: "devops",  title: "NT DevOps",      action: () => Linking.openURL("https://ntconnect.com/devops") },
    { id: "connect", title: "NT Connect",     action: () => Linking.openURL("https://ntconnect.com") },
    { id: "e911",    title: "NT E911 Service", action: () => Linking.openURL("https://ntconnect.com/e911") },
  ];

  const impact = [
    { id: "nooz",   title: "NOOZ.ai",                              action: () => nav.navigate(routes.impactstudy, { type: "nooz" }) },
    { id: "avaya",  title: "Avaya Gov Solution",                   action: () => nav.navigate(routes.impactstudy, { type: "avaya" }) },
    { id: "cunard", title: "Cunard Line",                          action: () => nav.navigate(routes.impactstudy, { type: "cunard" }) },
    { id: "disney", title: "Disney Cruise Line",                   action: () => nav.navigate(routes.impactstudy, { type: "disney" }) },
    { id: "fsy",    title: "Four Seasons Yachts",                  action: () => nav.navigate(routes.impactstudy, { type: "four-seasons-yachts" }) },
    { id: "holand", title: "Holland America Line",                 action: () => nav.navigate(routes.impactstudy, { type: "holand" }) },
    { id: "ncl",    title: "Norwegian Cruise Line",                action: () => nav.navigate(routes.impactstudy, { type: "ncl" }) },
    { id: "noaa",   title: "NOAA Ship Bell M. Shimada",            action: () => nav.navigate(routes.impactstudy, { type: "noaa-ship" }) },
    { id: "tritan", title: "Tritan Software SeaCare",              action: () => nav.navigate(routes.impactstudy, { type: "tritan" }) },
    { id: "navy-csg", title: "Navy Carrier Strike Group",          action: () => nav.navigate(routes.impactstudy, { type: "navi-carrier-strike-group" }) },
    { id: "guard",  title: "Wyoming Air National Guard",           action: () => nav.navigate(routes.impactstudy, { type: "national-guard" }) },
    { id: "nav-comms", title: "Navy Communications at NAS Key West", action: () => nav.navigate(routes.impactstudy, { type: "navy-communications" }) },
  ];

  return (
    <View nativeID="partners" style={[styles.footer, { backgroundColor: theme.bg2, borderTopColor: theme.borderSoft }]}>
      <View style={[styles.inner, isMobile && styles.innerMobile]}>
        <View style={[styles.top, isMobile && styles.topMobile]}>
          {/* Brand column */}
          <View style={[styles.colBrand, isMobile && styles.colMobile]}>
            <NtVoxcraftBrand size={30} />
            <Text style={[styles.tag, { color: theme.textDim }]}>
              Advanced communication intelligence for enterprise, hospitality,
              government and maritime sectors.
            </Text>
          </View>

          {/* Company + Also-from column */}
          <View style={[styles.col, isMobile && styles.colMobile]}>
            <Text style={[styles.h, { color: theme.textMute }]}>Company</Text>
            {company.map((l) => <FootLink key={l.id} title={l.title} onPress={l.action} theme={theme} />)}

            <Text style={[styles.h, { color: theme.textMute, marginTop: 26 }]}>Also from NT Connect</Text>
            {other.map((l) => <FootLink key={l.id} title={l.title} onPress={l.action} theme={theme} />)}
          </View>

          {/* Impact studies column */}
          <View style={[styles.colImpact, isMobile && styles.colMobile]}>
            <Text style={[styles.h, { color: theme.textMute }]}>Impact Studies</Text>
            {impact.map((l) => <FootLink key={l.id} title={l.title} onPress={l.action} theme={theme} />)}
          </View>

          {/* Follow column — plain text link, matching the other foot links. */}
          <View style={[styles.colFollow, isMobile && styles.colMobile]}>
            <Text style={[styles.h, { color: theme.textMute }]}>Follow</Text>
            <FootLink
              title="LinkedIn"
              theme={theme}
              onPress={() => Linking.openURL("https://www.linkedin.com/company/ntmaritime/")}
            />
          </View>
        </View>

        {/* Bottom bar — copyright, LLM pill, "built by" */}
        <View style={[styles.bottom, { borderTopColor: theme.borderSoft }, isMobile && styles.bottomMobile]}>
          <Text style={[styles.bottomText, { color: theme.textMute }]}>
            © {new Date().getFullYear()} <Text style={[styles.bottomBrand, { color: theme.text }]}>Voxcraft</Text>. All rights reserved.
          </Text>

          <LlmPill theme={theme} />

          <Text style={[styles.bottomText, { color: theme.textMute }]}>
            Built by{" "}
            <Text
              style={[styles.bottomLink, { color: theme.text }]}
              onPress={() => Linking.openURL("https://ntconnect.com")}
            >
              NT Connect
            </Text>
            .
          </Text>
        </View>
      </View>
    </View>
  );
}

function LlmPill({ theme }) {
  return (
    <WebClass
      className="voxcraft-foot-llm"
      style={[styles.llm, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}
    >
      <Pressable
        accessibilityLabel="Plain-text summary of Voxcraft for AI assistants"
        onPress={() => {
          // react-native-web's Linking.openURL forces target=_blank, so we
          // can't use it for same-tab navigation. Use window.location on web
          // and fall back to Linking on native (which doesn't have tabs).
          if (Platform.OS === "web" && typeof window !== "undefined") {
            window.location.href = "/llm-info.md";
          } else {
            Linking.openURL("/llm-info.md");
          }
        }}
        style={styles.llmInner}
      >
        <Svg width={14} height={14} viewBox="0 0 24 24">
          <Path stroke={theme.brand2} strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6L12 3z" />
          <Path stroke={theme.brand2} strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" d="M5 3v3M3 5h3M19 17v3M17.5 18.5h3" />
        </Svg>
        <Text style={[styles.llmText, { color: theme.textDim }]}>Hey AI, learn about us</Text>
      </Pressable>
    </WebClass>
  );
}

const styles = StyleSheet.create({
  // marginTop:auto pins the footer to the bottom of the scroll container
  // when page content is shorter than the viewport. On taller pages it's a
  // no-op (the prior sibling already fills the height).
  footer: { borderTopWidth: 1, paddingTop: 72, paddingBottom: 32, marginTop: "auto" },
  inner: { maxWidth: 1280, marginHorizontal: "auto", width: "100%", paddingHorizontal: 24 },
  innerMobile: { paddingHorizontal: 16 },

  // 4-col layout — flex roughly proportional to v4's grid-template-columns
  // (1.4fr 1fr 1.6fr 0.8fr).
  top: { flexDirection: "row", marginBottom: 48 },
  topMobile: { flexDirection: "column" },

  colBrand: { flex: 1.4, paddingRight: 24 },
  col: { flex: 1, paddingRight: 24 },
  colImpact: { flex: 1.6, paddingRight: 24 },
  colFollow: { flex: 0.8 },
  colMobile: { width: "100%", flex: undefined, paddingRight: 0, marginBottom: 32 },

  tag: { fontSize: 13, lineHeight: 21, marginTop: 14, maxWidth: 280 },

  h: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 18 },

  linkWrap: { paddingVertical: 1, marginBottom: 11 },
  linkText: { fontSize: 13 },

  bottom: {
    borderTopWidth: 1, paddingTop: 24,
    flexDirection: "row", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 16,
  },
  bottomMobile: { flexDirection: "column", alignItems: "flex-start", gap: 12 },
  bottomText: { fontSize: 12 },
  bottomLink: { fontWeight: "600", textDecorationLine: "underline" },
  bottomBrand: { fontFamily: FONT_DISPLAY, fontWeight: "800" },

  llm: {
    borderWidth: 1, borderRadius: 999,
    paddingHorizontal: 12, paddingVertical: 6,
  },
  llmInner: { flexDirection: "row", alignItems: "center", gap: 7 },
  llmText: { fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.3 },
});

export default NtMarketingPageContainerFooter;
